import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

/**
 * Reusable empty-state component. Pick one of the built-in illustrations
 * via `art` or pass a custom SVG element via `svg`. Style is friendly,
 * subtle indigo, minimal — never dominating the surrounding surface.
 */
export default function EmptyState({
  art = "inbox",
  svg,
  title,
  body,
  ctaLabel,
  ctaTo,
  onCta,
  testid = "empty-state",
}) {
  const Illustration = svg || ART[art] || ART.inbox;
  return (
    <div
      data-testid={testid}
      className="flex flex-col items-center justify-center gap-4 rounded-xl border border-dashed border-neutral-200 bg-white/60 px-6 py-12 text-center"
    >
      <div className="text-amber-400/70">
        {typeof Illustration === "function" ? <Illustration /> : Illustration}
      </div>
      <div className="max-w-sm space-y-1">
        <p className="text-sm font-semibold text-neutral-800">{title}</p>
        {body && <p className="text-xs leading-relaxed text-neutral-500">{body}</p>}
      </div>
      {ctaLabel && (ctaTo || onCta) && (
        ctaTo ? (
          <Button asChild size="sm" className="rounded-lg bg-amber-700 hover:bg-amber-800">
            <Link to={ctaTo} data-testid={`${testid}-cta`}>{ctaLabel}</Link>
          </Button>
        ) : (
          <Button size="sm" onClick={onCta} className="rounded-lg bg-amber-700 hover:bg-amber-800" data-testid={`${testid}-cta`}>
            {ctaLabel}
          </Button>
        )
      )}
    </div>
  );
}

// -- Simple inline SVG illustrations, single-color (currentColor) --
const ART = {
  inbox: () => (
    <svg width="88" height="72" viewBox="0 0 88 72" fill="none" aria-hidden="true">
      <path d="M8 26 L20 8 H68 L80 26 V56 A6 6 0 0 1 74 62 H14 A6 6 0 0 1 8 56 Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
      <path d="M8 26 H28 L32 34 H56 L60 26 H80" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" fill="none"/>
      <circle cx="44" cy="20" r="2" fill="currentColor" opacity="0.5"/>
    </svg>
  ),
  envelope: () => (
    <svg width="88" height="72" viewBox="0 0 88 72" fill="none" aria-hidden="true">
      <rect x="10" y="16" width="68" height="44" rx="6" stroke="currentColor" strokeWidth="2"/>
      <path d="M10 20 L44 42 L78 20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinejoin="round"/>
      <path d="M32 32 L18 50 M56 32 L70 50" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
    </svg>
  ),
  podium: () => (
    <svg width="96" height="72" viewBox="0 0 96 72" fill="none" aria-hidden="true">
      <path d="M34 26 H62 V62 H34 Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
      <path d="M6 42 H34 V62 H6 Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
      <path d="M62 36 H90 V62 H62 Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
      <path d="M48 18 L52 22 L56 18 L52 14 Z" fill="currentColor" opacity="0.65"/>
      <path d="M48 14 V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),
  wallet: () => (
    <svg width="88" height="72" viewBox="0 0 88 72" fill="none" aria-hidden="true">
      <rect x="8" y="18" width="72" height="42" rx="6" stroke="currentColor" strokeWidth="2"/>
      <path d="M8 26 H60 A6 6 0 0 1 66 32 V32 A6 6 0 0 1 60 38 H8" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" fill="none"/>
      <circle cx="60" cy="32" r="2.5" fill="currentColor"/>
    </svg>
  ),
  highfive: () => (
    <svg width="96" height="72" viewBox="0 0 96 72" fill="none" aria-hidden="true">
      <circle cx="24" cy="24" r="8" stroke="currentColor" strokeWidth="2"/>
      <circle cx="72" cy="24" r="8" stroke="currentColor" strokeWidth="2"/>
      <path d="M16 44 C16 36 32 36 32 44 V62 H16 Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
      <path d="M64 44 C64 36 80 36 80 44 V62 H64 Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
      <path d="M42 32 L54 20 M42 20 L54 32" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
    </svg>
  ),
  signpost: () => (
    <svg width="88" height="80" viewBox="0 0 88 80" fill="none" aria-hidden="true">
      <path d="M44 12 V70" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
      <path d="M14 22 H60 L68 30 L60 38 H14 Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
      <path d="M74 46 H28 L20 54 L28 62 H74 Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
      <path d="M32 76 H56" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
    </svg>
  ),
};
